import { config } from '@/config';
import { Environment, LINK, LocalStorageKey } from '@/constants';
import { PolarisBreakPoint } from '@/constants/enum';
import {
  getCrispTokenId,
  getIsHiddenChatFromAdmin,
  getLocalStorageItem,
  setLocalStorageItem,
  shouldResetCrispSession,
} from '@/helpers';
import { useGetUserPlanQuery } from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import { ChatboxPosition, Crisp } from 'crisp-sdk-web';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const useScript = () => {
  const { token, isAuth } = useSelector(tokenSelector);
  const { data: userPlanData } = useGetUserPlanQuery({});
  const isHiddenChatFromAdmin = getIsHiddenChatFromAdmin(token?.scope);
  const isNotShowCrisp = process.env.REACT_APP_ENV === Environment.Local || !isAuth || isHiddenChatFromAdmin;
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  useEffect(() => {
    if (isNotShowCrisp) return;
    const stamp = new Date().getTime();
    const crisp = document.createElement('script');
    crisp.onload = () => {
      setTimeout(() => {
        setIsScriptLoaded(true);
      }, 1000);
    };
    crisp.setAttribute('src', LINK.CHAT_PLUGIN(stamp));
    crisp.appendChild(document.createTextNode(``));
    const listScript = [crisp];

    // show crisp plugin
    const timerCrisp = setTimeout(
      () =>
        listScript.forEach((item) => {
          document.body.appendChild(item);
        }),
      3000,
    );

    return () => {
      clearTimeout(timerCrisp);
    };
  }, [isNotShowCrisp]);

  useEffect(() => {
    if (!isScriptLoaded) return;

    // push data to crisp
    let segments = ['order tracking'];
    const appName = 'Synctrack OrderTrack';
    const CRISP_WEBSITE_ID = 'a4bdf13a-e64f-4660-9d3a-b282560b6427';
    Crisp.configure(CRISP_WEBSITE_ID, {
      autoload: false,
    });
    if (userPlanData) {
      const shop = config.shop || userPlanData.data.shop || '';
      if (!shop) return; // R01: shop rỗng → KHÔNG bind (tránh token_id hằng số dùng chung)

      const tokenId = getCrispTokenId(shop);
      const lastTokenId = getLocalStorageItem(LocalStorageKey.CRISP_TOKEN_ID);
      if (shouldResetCrispSession(lastTokenId, tokenId)) {
        // H04: đổi shop trên cùng browser → reset session của shop trước, tránh ghi đè danh tính.
        // SDK Crisp.session.reset() là no-op khi Crisp chưa inject (isCrispInjected()=false trước load),
        // nên đẩy trực tiếp vào async queue window.$crisp để client replay khi boot — TRƯỚC khi bind danh tính mới.
        const crispQueue = (window as unknown as { $crisp?: unknown[][] }).$crisp || [];
        crispQueue.push(['do', 'session:reset']);
        (window as unknown as { $crisp?: unknown[][] }).$crisp = crispQueue;
      }
      setLocalStorageItem(LocalStorageKey.CRISP_TOKEN_ID, tokenId);

      const {
        shopifyPlan,
        appPlan,
        merchantVertical,
        email,
        country,
        dateInstalled,
        lastInstalled,
        storeName,
        storeCreatedAt,
        reviewHistory,
      } = userPlanData.data.crispData;
      Crisp.setTokenId(tokenId);
      Crisp.user.setEmail(email);
      Crisp.user.setNickname(shop);
      Crisp.session.setData({
        app_name: appName,
        shopify_plan: shopifyPlan,
        app_plan: appPlan,
        merchant_vertical: merchantVertical,
        store_url: `https://${shop}`,
        email_store: email,
        country: country,
        date_installed: dayjs(dateInstalled).format('MMM DD, YYYY hh:mm A'),
        last_installed: dayjs(lastInstalled).format('MMM DD, YYYY hh:mm A'),
        store_name: storeName,
        store_created_at: dayjs(storeCreatedAt).format('MMM DD, YYYY hh:mm A'),
        review_history: reviewHistory,
      });

      // get app plan segment
      let appPlanSegment;
      const normalizedPlan = appPlan.toLowerCase();
      if (normalizedPlan === 'free') {
        appPlanSegment = 'new customers';
      } else if (['launch', 'scale'].includes(normalizedPlan)) {
        appPlanSegment = 'premium customers';
      } else if (normalizedPlan === 'pro') {
        appPlanSegment = 'VIP customers';
      }
      if (appPlanSegment) segments.push(appPlanSegment);
    }

    Crisp.session.setSegments([...segments], true);
    Crisp.load();
    Crisp.setPosition(ChatboxPosition.Right);
  }, [isScriptLoaded, userPlanData]);

  // BFS412 Bug 2: trên mobile (≤ PolarisBreakPoint.SM) ẩn RIÊNG bong bóng Crisp nổi
  // (tránh đè CTA — mục tiêu cũ của TR-438) nhưng vẫn cho mở cửa sổ chat khi merchant
  // bấm support (helper đẩy chat:show + chat:open). Đóng chat xong thì ẩn bong bóng lại.
  // Desktop/tablet (> SM) giữ bong bóng như cũ. Thay cho cách cũ `display:none` (ẩn cả
  // cửa sổ chat) trong index.css.
  useEffect(() => {
    if (!isScriptLoaded) return undefined;
    const crisp = (window as any).$crisp;
    if (!crisp || typeof window.matchMedia !== 'function') return undefined;

    const mql = window.matchMedia(`(max-width: ${PolarisBreakPoint.SM})`);
    const applyBubbleVisibility = () => crisp.push(['do', mql.matches ? 'chat:hide' : 'chat:show']);

    applyBubbleVisibility();
    // Mở chat (chat:show) làm bong bóng hiện lại; ẩn lại sau khi user đóng trên mobile.
    crisp.push(['on', 'chat:closed', () => mql.matches && crisp.push(['do', 'chat:hide'])]);
    mql.addEventListener('change', applyBubbleVisibility);

    return () => mql.removeEventListener('change', applyBubbleVisibility);
  }, [isScriptLoaded]);

  return null;
};
export default useScript;
